/**
 * M24 task-09 (DBC-12) — the Relations list, reworked from the static
 * placeholder into a real table over GET /relations. Server-side pagination
 * AND filtering: the filter toolbar maps to refine `eq` filters which the
 * data provider forwards as RelationListQuery params — nothing is filtered
 * client-side. Per-row delete goes through the same tuple-body DELETE the
 * Create page uses.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTable } from '@refinedev/react-table';
import { type ColumnDef } from '@tanstack/react-table';
import type { CrudFilters } from '@refinedev/core';
import { Link } from 'react-router';
import { Trash2 } from 'lucide-react';
import type { RelationListItem } from '@verybigthings/trackframe-shared/schemas';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { fmtDateTime } from '@/lib/format';
import { hub } from '@/lib/hub-client';
import { filterFlag, filterValue } from '@/lib/list-filters';
import { cn } from '@/lib/utils';
import {
  relationAfterTypeChange,
  relationOptionsFor,
  useNamespaceRelations,
} from './namespace-relations';

/**
 * A tuple ref with its human label above the raw ref (exported test seam).
 * The ref is ALWAYS rendered — it is what an operator pastes back into the
 * filter boxes, and it carries the `#relation` suffix of a userset subject.
 * No label means the hub could not resolve it (dangling tuple, or a type with
 * no name to show); the bare ref then stands on its own rather than being
 * replaced by an invented one.
 */
export function RefCell({ refString, label }: { refString: string; label?: string }) {
  return (
    <div className="leading-tight">
      {label && <div className="truncate">{label}</div>}
      <code
        className={label ? 'font-mono text-[11px] text-muted-foreground' : 'font-mono text-xs'}
      >
        {refString}
      </code>
    </div>
  );
}

/**
 * What the revoke confirmation says (exported test seam). Names first, because
 * that is what an operator can actually check — but the raw refs stay, since
 * two documents may share a filename and only the id tells them apart.
 */
export function describeTuple(row: RelationListItem): string {
  const object = row.objectLabel ? `${row.objectLabel} (${row.object})` : row.object;
  const subject = row.subjectLabel ? `${row.subjectLabel} (${row.subject})` : row.subject;
  return `${object} · ${row.relation} · ${subject} will be revoked.`;
}

/** Column factory (exported test seam) — the delete cell needs a callback. */
export function buildRelationColumns({
  onDelete,
}: {
  onDelete: (row: RelationListItem) => void;
}): ColumnDef<RelationListItem>[] {
  return [
    {
      id: 'object',
      header: 'Object',
      accessorKey: 'object',
      cell: (c) => <RefCell refString={c.getValue() as string} label={c.row.original.objectLabel} />,
    },
    {
      id: 'relation',
      header: 'Relation',
      accessorKey: 'relation',
    },
    {
      id: 'subject',
      header: 'Subject',
      accessorKey: 'subject',
      cell: (c) => <RefCell refString={c.getValue() as string} label={c.row.original.subjectLabel} />,
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessorKey: 'createdAt',
      cell: (c) => (
        <span className="tabular-nums text-muted-foreground">{fmtDateTime(c.getValue() as string)}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (c) => (
        <div className="flex justify-end">
          <Button
            size="icon"
            variant="ghost"
            className="size-8"
            onClick={() => onDelete(c.row.original)}
          >
            <Trash2 className="size-4" aria-hidden="true" />
            <span className="sr-only">Delete</span>
          </Button>
        </div>
      ),
    },
  ];
}

export interface RelationsFilterToolbarProps {
  objectTypes: string[];
  objectType: string;
  setObjectType: (value: string) => void;
  relationOptions: string[];
  search: string;
  setSearch: (value: string) => void;
  relation: string;
  setRelation: (value: string) => void;
  showStructural: boolean;
  setShowStructural: (value: boolean) => void;
}

/** Props-only filter toolbar (exported test seam). */
export function RelationsFilterToolbar({
  objectTypes,
  objectType,
  setObjectType,
  relationOptions,
  search,
  setSearch,
  relation,
  setRelation,
  showStructural,
  setShowStructural,
}: RelationsFilterToolbarProps) {
  return (
    <>
      {objectTypes.length > 0 ? (
        <Select
          aria-label="Object type"
          className="h-8 shadow-none"
          value={objectType}
          onChange={(e) => setObjectType(e.target.value)}
        >
          <option value="">All object types</option>
          {objectTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
      ) : (
        // Namespaces unavailable (fetch failed) — free text, Create.tsx idiom.
        <Input
          aria-label="Object type"
          placeholder="Object type"
          className="h-8 w-36 shadow-none"
          value={objectType}
          onChange={(e) => setObjectType(e.target.value)}
        />
      )}
      {/* One box for both sides: the hub matches a resolved label (a filename,
          a user's name or email, a track) or a raw-id substring, in any
          namespace, so a pasted id still works. */}
      <Input
        aria-label="Search"
        placeholder="Search names or ids"
        className="h-8 w-64 shadow-none"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {objectTypes.length > 0 ? (
        <Select
          aria-label="Relation"
          className="h-8 shadow-none"
          value={relation}
          onChange={(e) => setRelation(e.target.value)}
        >
          <option value="">All relations</option>
          {relationOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      ) : (
        // Same fallback as the object type: with no namespace data there is
        // no closed set to offer, so free text beats an empty dropdown.
        <Input
          aria-label="Relation"
          placeholder="Relation"
          className="h-8 w-36 shadow-none"
          value={relation}
          onChange={(e) => setRelation(e.target.value)}
        />
      )}
      {/* Containment (`parent`) tuples are written by the system as objects
          are created — they are the object graph, not grants, and they
          outnumber real grants many times over. Off by default. */}
      <label className="flex items-center gap-2 whitespace-nowrap text-sm text-muted-foreground">
        <input
          type="checkbox"
          aria-label="Show system-generated"
          checked={showStructural}
          onChange={(e) => setShowStructural(e.target.checked)}
        />
        Show system-generated
      </label>
    </>
  );
}

export interface RelationsFilterState {
  objectType: string;
  search: string;
  relation: string;
  showStructural: boolean;
}

/**
 * Toolbar state ⇄ refine filters. Both directions are needed: the URL is the
 * only place the toolbar survives a reload (list-filters.test).
 */
export function relationsCrudFilters({
  objectType,
  search,
  relation,
  showStructural,
}: RelationsFilterState): CrudFilters {
  const filters: CrudFilters = [];
  if (objectType.trim()) filters.push({ field: 'objectType', operator: 'eq', value: objectType.trim() });
  if (search.trim()) filters.push({ field: 'q', operator: 'eq', value: search.trim() });
  if (relation.trim()) filters.push({ field: 'relation', operator: 'eq', value: relation.trim() });
  // Always emitted, even though the route treats it as opt-OUT: "on" has to be
  // distinguishable from "no filters in the URL at all", or a reload cannot
  // tell an explicit Show system-generated from a first visit — which defaults
  // to hiding them.
  filters.push({ field: 'includeStructural', operator: 'eq', value: showStructural });
  return filters;
}

export function relationsFilterState(filters: CrudFilters | undefined): RelationsFilterState {
  return {
    objectType: filterValue(filters, 'objectType'),
    search: filterValue(filters, 'q'),
    relation: filterValue(filters, 'relation'),
    // Absent → a first visit, which hides structural tuples.
    showStructural: filterFlag(filters, 'includeStructural') ?? false,
  };
}

export function RelationsList() {
  // Both select sources come from the ONE /relations/namespaces call: object
  // types are its keys, relation options its `directRelations`.
  const namespaceRelations = useNamespaceRelations();
  const [pendingDelete, setPendingDelete] = useState<RelationListItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const columns = useMemo(() => buildRelationColumns({ onDelete: setPendingDelete }), []);
  const {
    reactTable: table,
    refineCore: { setFilters, filters, tableQuery },
  } = useTable<RelationListItem>({ columns });
  // Seeded from the filters refine restored from the URL — otherwise the
  // effect below fires on mount with empty values and erases them.
  const [state, setState] = useState<RelationsFilterState>(() => relationsFilterState(filters));
  const { objectType, search, relation, showStructural } = state;
  // The hub caps how many objects one search term may resolve to; when it
  // does, say so rather than presenting a short list as the whole truth.
  const searchTruncated = (tableQuery.data as { searchTruncated?: boolean } | undefined)?.searchTruncated;

  const objectTypes = useMemo(() => Object.keys(namespaceRelations).sort(), [namespaceRelations]);
  const relationOptions = useMemo(
    () => relationOptionsFor(namespaceRelations, objectType),
    [namespaceRelations, objectType],
  );

  // Narrowing the object type must not leave a relation behind that the new
  // type cannot have — that would silently filter the list down to nothing.
  const onObjectType = (value: string) => {
    setState((s) => ({
      ...s,
      objectType: value,
      relation: relationAfterTypeChange(relationOptionsFor(namespaceRelations, value), s.relation),
    }));
  };

  // Debounced server-side filters (users/List idiom) — `eq` filters the
  // data provider maps onto RelationListQuery params.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters(relationsCrudFilters(state), 'replace');
    }, 250);
    return () => clearTimeout(handle);
  }, [state, setFilters]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      // Exact tuple body shape as Create.tsx's delete branch.
      const body = {
        object: pendingDelete.object,
        relation: pendingDelete.relation,
        subject: pendingDelete.subject,
      };
      await hub.relationsDelete({ body });
      setPendingDelete(null);
      await tableQuery.refetch();
    } catch (err) {
      setDeleteError((err as Error).message);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Permissions</h1>
          <p className="text-sm text-muted-foreground">
            Zanzibar tuples — browse, write, and revoke relationships between objects, relations, and subjects.
          </p>
        </div>
        <Link to="/admin/permissions/create" className={cn(buttonVariants())}>
          New relation
        </Link>
      </div>
      {deleteError && <p className="text-sm text-destructive">{deleteError}</p>}
      {searchTruncated && (
        <p className="text-sm text-muted-foreground">
          Too many objects match “{search.trim()}” — some tuples are missing from this list. Narrow the
          search or pick an object type.
        </p>
      )}
      <DataTable
        table={table}
        toolbar={
          <RelationsFilterToolbar
            objectTypes={objectTypes}
            objectType={objectType}
            setObjectType={onObjectType}
            relationOptions={relationOptions}
            search={search}
            setSearch={(next) => setState((s) => ({ ...s, search: next }))}
            relation={relation}
            setRelation={(next) => setState((s) => ({ ...s, relation: next }))}
            showStructural={showStructural}
            setShowStructural={(next) => setState((s) => ({ ...s, showStructural: next }))}
          />
        }
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete tuple?"
        description={pendingDelete ? describeTuple(pendingDelete) : undefined}
        confirmLabel="Delete"
        destructive
        busy={deleteBusy}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
